import { useNavigate, Navigate, useLocation } from 'react-router-dom'
import ROUTES from '../../routesConfig'
import AuthTokenExpiredError from '../Api/AuthTokenExpiredError'
import GuestReestablishRequiredError from '../Api/GuestReestablishRequiredError'
import UserAuthContainer from '../Containers/UserAuthContainer'
import TokenStorage from '../Api/TokenStorage'

const useNavigation = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const userAuthContainer = UserAuthContainer.useContainer()
  const tokenStorage = new TokenStorage()

  const sanitizeNavigationState = (value, depth = 0) => {
    if (value === null || value === undefined) return value
    if (depth > 6) return null

    const valueType = typeof value
    if (valueType === 'string' || valueType === 'number' || valueType === 'boolean') return value
    if (valueType === 'function' || valueType === 'symbol' || valueType === 'bigint') return undefined

    if (Array.isArray(value)) {
      return value
        .map((item) => sanitizeNavigationState(item, depth + 1))
        .filter((item) => item !== undefined)
    }

    if (value instanceof Date) return value.toISOString()
    if (value instanceof Error) return { name: value.name, message: value.message }

    const proto = Object.getPrototypeOf(value)
    if (proto !== Object.prototype && proto !== null) {
      // Class instances (e.g. ErrorResponse) are not clone-safe for history state.
      if (value.message && typeof value.message === 'string') return { message: value.message }
      return undefined
    }

    const output = {}
    Object.entries(value).forEach(([key, nestedValue]) => {
      const nextValue = sanitizeNavigationState(nestedValue, depth + 1)
      if (nextValue !== undefined) output[key] = nextValue
    })
    return output
  }

  const normalizeNavigateTarget = (target) => {
    if (!target || typeof target === 'string') {
      return { to: target, state: undefined }
    }

    return {
      to: {
        pathname: target.pathname,
        search: target.search,
        hash: target.hash,
      },
      state: sanitizeNavigationState(target.state),
    }
  }
  const resolveHomePath = () => {
    if (userAuthContainer?.isMerchant?.()) return ROUTES.MERCHANT_DASHBOARD
    if (tokenStorage.hasActiveVenueHomeContext()) {
      const venueContext = tokenStorage.getVenueHomeContext()
      if (venueContext?.venueSlug) return ROUTES.VENUE_PAGE(venueContext.venueSlug)
    }
    return ROUTES.APPS
  }


  const navigateToPathWithReplace = (path, replace = true) => {
    const normalized = normalizeNavigateTarget(path)
    navigate(normalized.to, { replace, state: normalized.state })
  }

  // Navigate to specific routes
  const navigateToApps = () => {
    navigateToPathWithReplace(ROUTES.APPS)
  }

  const navigateToEvents = () => {
    navigateToPathWithReplace(ROUTES.EVENTS)
  }
  const navigateToMerchantDashboard = () => {
    navigateToPathWithReplace(ROUTES.MERCHANT_DASHBOARD)
  }
  const navigateToLogin = (from = null) => {
    if (from) {
      const normalizedFrom = normalizeNavigateTarget(from)
      navigate(ROUTES.LOGIN, {
        state: {
          from: normalizedFrom.state !== undefined
            ? { ...normalizedFrom.to, state: normalizedFrom.state }
            : normalizedFrom.to
        }
      })
      return
    }
    navigate(ROUTES.LOGIN)
  }

  const navigateToSignup = (from = null, extraState = {}) => {
    if (from) {
      const normalizedFrom = normalizeNavigateTarget(from)
      navigate(ROUTES.SIGNUP, {
        state: {
          from: normalizedFrom.state !== undefined
            ? { ...normalizedFrom.to, state: normalizedFrom.state }
            : normalizedFrom.to,
          ...sanitizeNavigationState(extraState)
        }
      })
      return
    }
    navigate(ROUTES.SIGNUP, { state: sanitizeNavigationState(extraState) })
  }

  const navigateToForgotPassword = () => {
    navigate(ROUTES.FORGOT_PASSWORD)
  }

  const navigateToProfile = () => {
    navigate(ROUTES.PROFILE)
  }

  const navigateToUpdateProfile = () => {
    navigateToPathWithReplace(ROUTES.UPDATE_PROFILE)
  }

  const navigateToScan = () => {
    navigate(ROUTES.SCAN)
  }

  const navigateToHome = () => {
    navigateToPathWithReplace(resolveHomePath())
  }

  const navigateToGuestSignup = (from, guestSignupContext = {}) => {
    const normalizedFrom = normalizeNavigateTarget(from)
    navigate(ROUTES.GUEST_SIGNUP, {
      state: {
        from: normalizedFrom.state !== undefined
          ? { ...normalizedFrom.to, state: normalizedFrom.state }
          : normalizedFrom.to,
        guestSignupContext: sanitizeNavigationState(guestSignupContext),
      }
    })
  }

  const navigateToEventParticipant = (eventId, options = {}) => {
    navigate(ROUTES.EVENT_PARTICIPANT(eventId), { state: { ...options } })
  }

  const navigateToEvent = (eventSlug, state = undefined) => {
    navigate(ROUTES.EVENT_PAGE(eventSlug), { replace: true, state })
  }

  const navigateToVenue = (venueSlug) => {
    navigateToPathWithReplace(ROUTES.VENUE_PAGE(venueSlug))
  }

  const navigateToCreateEvent = (zoneId) => {
    navigate(ROUTES.CREATE_EVENT(zoneId))
  }

  const navigateToEventPage = (shortUrl) => {
    navigateToPathWithReplace(shortUrl)
  }

  const navigateToScanPage = () => {
    navigateToPathWithReplace(ROUTES.SCAN)
  }

  // Function to return a JSX component for navigation
  const getNavigateComponent = (path, replace = true) => {
    const normalized = normalizeNavigateTarget(path)
    return <Navigate to={normalized.to} replace={replace} state={normalized.state} />
  }

  const getNavigateToEventComponent = (eventId) => {
    return getNavigateComponent(ROUTES.EVENT_PAGE(eventId))
  }

  //
  // Centralized handler for auth-token expiry so that any page making
  // authenticated API calls can gracefully recover by clearing stale
  // auth and sending the user through the right flow.
  //
  const handleAuthTokenExpired = (err, userAuthContainer) => {
    if (!(err instanceof AuthTokenExpiredError)) return false

    if (err instanceof GuestReestablishRequiredError) {
      const normalizedLocation = normalizeNavigateTarget(location)
      navigate(ROUTES.GUEST_EXPIRED, {
        state: {
          from: normalizedLocation.state !== undefined
            ? { ...normalizedLocation.to, state: normalizedLocation.state }
            : normalizedLocation.to,
          reestablishMeta: err.meta || null,
        },
      })
      return true
    }

    let type = 'unknown'
    try {
      if (userAuthContainer.hasRegisteredToken()) {
        type = 'registered'
      } else if (userAuthContainer.hasAuthToken()) {
        type = 'guest'
      }
    } catch (_) {
      // fall through with 'unknown'
    }

    userAuthContainer.clearUserAndToken()

    if (type === 'registered') {
      const normalizedLocation = normalizeNavigateTarget(location)
      navigate(ROUTES.LOGIN, {
        state: {
          from: normalizedLocation.state !== undefined
            ? { ...normalizedLocation.to, state: normalizedLocation.state }
            : normalizedLocation.to
        }
      })
    } else {
      const normalizedLocation = normalizeNavigateTarget(location)
      navigate(ROUTES.GUEST_EXPIRED, {
        state: {
          from: normalizedLocation.state !== undefined
            ? { ...normalizedLocation.to, state: normalizedLocation.state }
            : normalizedLocation.to,
          notice: 'guest_expired',
        },
      })
    }

    return true
  }

  // Add more navigation functions as needed based on your specific routes and usage patterns

  return {
    navigateToApps,
    navigateToLogin,
    navigateToSignup,
    navigateToForgotPassword,
    navigateToProfile,
    navigateToUpdateProfile,
    navigateToScan,
    navigateToHome,
    navigateToGuestSignup,
    navigateToEventParticipant,
    navigateToEvent,
    navigateToVenue,
    navigateToEvents,
    navigateToMerchantDashboard,
    navigateToCreateEvent,
    navigateToEventPage,
    navigateToScanPage,
    navigateToPathWithReplace,
    resolveHomePath,
    getNavigateComponent,
    getNavigateToEventComponent,
    handleAuthTokenExpired,
  }
}

export default useNavigation
